









































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { InputFundraisingGoalRewardCreate } from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import useBlobUrl from "@/hooks/useBlobUrl";
import { $t } from "@/lib/i18nHelpers";
import { FormFields, FormValues } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseForm from "@/components/BaseForm";
import BaseInputFileWithPreview from "@/components/BaseInputFileWithPreview";
import { InputRewardCreate } from "@/components/FundraisingGoal/types";
import ImagePreview from "@/components/ImagePreview";

export default defineComponent({
  name: "CreateRewardModal",
  components: { ImagePreview, BaseButton, BaseInputFileWithPreview, BaseForm, BaseConfirm },
  props: {
    show: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    reward: { type: Object as PropType<InputRewardCreate> },
  },
  setup(props, { emit }) {
    const { getBlobUrl, revokeBlobs } = useBlobUrl();
    const title = computed(() => (props.reward ? $t("Edit Reward") : $t("Add reward")));
    const valid = ref(false);
    const srcUrl = ref<string | null>(null);
    let file: File | null;

    const values = ref<FormValues>({});

    const fields = computed<FormFields>(() => [
      {
        name: "title",
        label: $t("Reward"),
        type: "text",
        rules: ["required"],
        validationMode: "aggressive",
        maxlength: "255",
      },
      {
        name: "description",
        label: $t("Description"),
        type: "text",
        maxlength: "2047",
      },
      {
        name: "amount",
        label: $t("Cost"),
        type: "money",
        rules: ["required"],
        validationMode: "aggressive",
      },
      {
        name: "totalCount",
        label: $t("Number of rewards"),
        inputmode: "numeric",
        rules: {
          required: !values.value.unlimitedRewards,
          min_value: "1",
          max_value: String(Math.pow(2, 31) - 1),
          numeric: true,
        },
        validationMode: "aggressive",
        disabled: values.value.unlimitedRewards,
        autocomplete: "off",
        maskOpts: {
          regex: "\\d*",
        },
      },
      {
        name: "unlimitedRewards",
        label: $t("Unlimited rewards"),
        type: "checkbox",
        class: "mt-n2",
      },
    ]);

    const onAddFiles = ([newFile]: File[]) => {
      file = newFile;
      srcUrl.value = getBlobUrl(file);
    };

    const onRemoveFiles = () => {
      file = null;
      srcUrl.value = null;
    };

    const reset = () => {
      values.value = {};
      file = null;
      srcUrl.value = null;
      revokeBlobs();
    };

    const onSubmit = () => {
      const { title, description, amount, totalCount, unlimitedRewards } = values.value;
      const data: Omit<InputFundraisingGoalRewardCreate, "goalId"> = {
        title,
        description: description ?? "",
        amount: +amount,
        totalCount: unlimitedRewards ? undefined : +totalCount,
      };

      emit("submit", { data, file });
    };

    watch(toRef(props, "show"), show => {
      if (show) {
        if (props.reward) {
          const { title, description, amount, totalCount } = props.reward.data;
          values.value = {
            title,
            description,
            amount: String(amount),
            totalCount: totalCount ? String(totalCount) : undefined,
            unlimitedRewards: !totalCount,
          };
          props.reward.file && onAddFiles([props.reward.file]);
        }
      } else reset();
    });
    return {
      title,
      fields,
      values,
      srcUrl,
      valid,
      onAddFiles,
      onRemoveFiles,
      onSubmit,
    };
  },
});
